import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  ActionIconDef, Grid, GridActionIconClickEvent, GridConfig, GridHyperlinkClickEvent, GridRow, GridToolbarActionClickEvent,
} from '@inavista/components/grid';
import { APP_CONFIG, AppConfig } from 'projects/shared/src/lib/app-config.token';
import { Applications, RolePermissionsModel } from '../../../../shared/models/app.model';
import { UserRoles } from '../../../../shared/constants/app.constants';
import { CommonService } from '../../../../shared/services/common.service';
import { UPGeneralFields } from '../../model/user-profile.model';
import { UserProfileService } from '../../services/user-profile.service';
import { DirectReportsComponent } from './direct-reports/direct-reports.component';
import { UsersGridContext, UsersTab, usersActionIcons, usersGridConfig, usersToolbarActions } from './users.grid.config';

// Users page (Sales user-profile module, routed in RIMS Service as /home/users too), on @inavista/components: the rows come
// from RIMS Master api/grid/users (users.grid.config.ts). Same tabs, permissions, row actions, links and popups as the
// Syncfusion page it replaces. Eager, not OnPush: it hosts the unchanged Eager user-profile popup.
@Component({
    selector: 'app-users-sales',
    templateUrl: './users.component.html',
    styleUrl: './users.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UsersComponent implements OnInit {
  @ViewChild('usersGrid') grid?: Grid;
  @ViewChild('territoryCell', { static: true }) territoryCell!: TemplateRef<unknown>;
  @ViewChild('districtCell', { static: true }) districtCell!: TemplateRef<unknown>;
  @ViewChild('regionCell', { static: true }) regionCell!: TemplateRef<unknown>;

  private readonly cdr = inject(ChangeDetectorRef);

  userProfile: any;
  showUserProfilePopup = false;
  PERMISSIONS: RolePermissionsModel[] = [];
  selectedTab: UsersTab;
  gridConfig: GridConfig | null = null;
  actionIcons: ActionIconDef[] = [];

  // The grid fills the window from where it starts down to the page frame's bottom padding, as Service Orders.
  private static readonly BottomFramePx = 15;
  readonly gridPageHeight = signal(window.innerHeight - 120);
  private gridPageEl?: HTMLElement;
  // The wrapper appears once the permissions are known, so it is measured when it does.
  @ViewChild('gridPage') set gridPage(ref: ElementRef<HTMLElement> | undefined) {
    this.gridPageEl = ref?.nativeElement;
    if (this.gridPageEl) setTimeout(() => this.fitGridToWindow());
  }

  @HostListener('window:resize')
  fitGridToWindow() {
    const top = this.gridPageEl?.getBoundingClientRect().top;
    if (top === undefined) return;
    this.gridPageHeight.set(Math.max(300, Math.floor(window.innerHeight - top - UsersComponent.BottomFramePx)));
  }

  constructor(
    @Inject(APP_CONFIG) private config: AppConfig,
    public dialog: MatDialog,
    private commonService: CommonService,
    private usersService: UserProfileService,
  ) {}

  ngOnInit(): void {
    this.loadPermissions();
  }

  getPermission(ruleAction: string): RolePermissionsModel {
    return this.PERMISSIONS?.find((e: RolePermissionsModel) => e.ruleAction === ruleAction)!;
  }

  // The usersGrid permissions of the caller's role (app.rolePermissions), then the tab: the one the user last chose when
  // they may still see it, else Service, else Sales (as before, remembered in this browser).
  loadPermissions(category = 'usersGrid', subCategory = '', id = '', module = 'userProfile') {
    this.commonService.getRolePermission(category, subCategory, module, id).subscribe({
      next: (res: any) => {
        this.PERMISSIONS = res?.result ?? [];
        const canService = !!this.getPermission('UsersGrid_serviceUsersTab')?.visible;
        const canSales = !!this.getPermission('UsersGrid_salesUsersTab')?.visible;
        const saved = localStorage.getItem('selectedTab_UserProfileGrid') as UsersTab | null;
        const tab: UsersTab | null = (saved === 'sales' && canSales) || (saved === 'service' && canService) ? saved
          : canService ? 'service' : canSales ? 'sales' : null;
        if (tab) localStorage.setItem('selectedTab_UserProfileGrid', tab);
        else localStorage.removeItem('selectedTab_UserProfileGrid');
        this.showTab(tab);
      },
      error: (err: any) => {
        this.PERMISSIONS = [];
        this.commonService.showNotification('error', err?.error?.message ?? 'The users permissions could not be loaded. Refresh the page to try again.');
      },
    });
  }

  private showTab(tab: UsersTab | null) {
    this.selectedTab = tab!;
    if (!tab) {
      this.gridConfig = null;
      return;
    }
    const permission = (key: string) => ({ visible: !!this.getPermission(key)?.visible, enable: !!this.getPermission(key)?.enable });
    const exportPermission = permission('UsersGrid_ExcelExport');
    const role = this.commonService.getUSER()?.role;
    const ctx: UsersGridContext = {
      masterApiUrl: this.config.masterApiURL,
      tab,
      showIscCode: ![UserRoles.klNationalMGR, UserRoles.klServiceManager].includes(role!),
      canAddUser: permission('UsersGrid_AddNewUser'),
      canExport: exportPermission.visible && exportPermission.enable && role !== UserRoles.salesRep,
      canResetPersistence: permission('UsersGrid_ResetUserPersistence'),
      orgTemplates: { territory: this.territoryCell, district: this.districtCell, region: this.regionCell },
    };
    this.gridConfig = { ...usersGridConfig(ctx), toolbarActions: usersToolbarActions(ctx) };
    this.actionIcons = usersActionIcons(ctx);
    this.cdr.markForCheck();
  }

  tabClick(tab: UsersTab) {
    if (tab === this.selectedTab) return;
    localStorage.setItem('selectedTab_UserProfileGrid', tab);
    this.showTab(tab);
  }

  private app(): Applications {
    return this.selectedTab === 'service' ? Applications.RimsService : Applications.RimsSales;
  }

  onToolbarAction(event: GridToolbarActionClickEvent) {
    if (event.key === 'addUser') {
      this.userProfile = {};
      this.showUserProfilePopup = true;
    } else if (event.key === 'refresh') {
      this.loadPermissions();
    }
  }

  onHyperlinkClick(event: GridHyperlinkClickEvent) {
    this.openProfile(event.row);
  }

  onActionIconClick(event: GridActionIconClickEvent) {
    const row = event.row;
    switch (event.iconKey) {
      case 'edit':
        this.openProfile(row);
        break;
      case 'resetPersistence':
        this.commonService.showConfirmation('This will clear the persistence of this user, Do you want to proceed?').then(result => {
          if (!result.isConfirmed) return;
          this.usersService.clearPersistence(String(row[UPGeneralFields.userId]), '').subscribe({
            next: (res: any) => this.commonService.showNotification('info', res.message),
            error: (err: any) => this.commonService.showNotification('error', err?.error?.message),
          });
        });
        break;
      case 'directReports':
        this.dialog.open(DirectReportsComponent, {
          disableClose: false, maxWidth: '720px', maxHeight: '520px', width: '100%', height: '100%',
          enterAnimationDuration: '0', exitAnimationDuration: '0',
          data: { app: row['applicationsList'], userId: row[UPGeneralFields.userId] },
        });
        break;
      case 'markInactive':
        this.commonService.showConfirmation(`Are you sure to disable ${row['name']}?`).then((res: any) => {
          if (res?.isConfirmed) this.enableDisableApp(String(row[UPGeneralFields.userId]), false);
        });
        break;
      case 'markActive':
        this.enableDisableApp(String(row[UPGeneralFields.userId]), true);
        break;
    }
  }

  private openProfile(row: GridRow) {
    this.userProfile = { ...row };
    this.showUserProfilePopup = true;
  }

  private enableDisableApp(userId: string, toggle: boolean) {
    this.usersService.enableDisableApplication(userId, this.app(), toggle).subscribe({
      next: (res: any) => {
        this.grid?.refresh();
        this.commonService.showNotification('success', res?.message);
      },
      error: (err: any) => this.commonService.showNotification('error', err?.error?.message),
    });
  }

  // As before: closing the profile reloads the permissions and, with them, the list (a new config reloads the grid).
  onClosePopup() {
    this.showUserProfilePopup = false;
    this.userProfile = null;
    this.loadPermissions();
  }
}
